import { Link } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useAuth } from '../auth/AuthContext';
import { useSettings } from '../settings/SettingsContext';
import { Loading, LoadError } from '../components/Loading';
import type { Language, LanguageProgress } from '../../../shared/types';

function StatTile({ value, label }: { value: string | number; label: string }) {
  return (
    <div className="stat-tile">
      <span className="stat-value">{value}</span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/**
 * One language the learner is studying: where they left off, and what is
 * waiting for them.
 *
 * `progress` is absent for a language added but never opened — the API reports
 * only languages with a history, so there is no row of zeroes to distinguish
 * from real ones. That case renders as the invitation to start.
 */
function LanguageCard({
  language,
  progress,
}: {
  language: Language;
  progress: LanguageProgress | undefined;
}) {
  const last = progress?.lastSection;
  const due = progress?.dueCount ?? 0;

  return (
    <section className="home-language">
      <header className="home-language-head">
        <span className="tile-emblem" aria-hidden="true">
          {language.emblem}
        </span>
        <span className="home-language-name">
          <Link to={`/languages/${language.code}`}>{language.name}</Link>
          <span className="tile-native">{language.nativeName}</span>
        </span>
        {progress && (
          <span className="home-language-known muted">{progress.cardsKnown} words known</span>
        )}
      </header>

      {last ? (
        <p className="home-language-last muted">
          Last read: chapter {last.chapterNumber} · {last.number} {last.title}
        </p>
      ) : (
        <p className="home-language-last muted">Not started yet.</p>
      )}

      <div className="home-language-actions">
        {last ? (
          <Link to={`/sections/${last.sectionId}`} className="btn btn--primary">
            Carry on — {last.number} {last.title}
          </Link>
        ) : (
          <Link to={`/languages/${language.code}`} className="btn btn--primary">
            Start the first book
          </Link>
        )}

        {/* Offered only when there is something to review. A review button that
            opens an empty drill teaches nothing and wastes the tap. */}
        {due > 0 && (
          <Link to={`/review/${language.code}`} className="btn">
            Review {due} due
          </Link>
        )}

        <Link to={`/build/${language.code}`} className="btn">
          Build a session
        </Link>
      </div>
    </section>
  );
}

export default function HomePage() {
  const api = useApi();
  const { user } = useAuth();
  const { settings } = useSettings();

  const languages = useAsync(() => catalogApi.languages(api), [api]);
  const stats = useAsync(() => progressApi.stats(api), [api]);

  const firstName = (user?.displayName ?? '').split(' ')[0];

  // The catalog, keyed so a studied language can be looked up by code.
  const byCode = new Map((languages.data?.languages ?? []).map((l) => [l.code, l]));
  const progressByCode = new Map((stats.data?.languages ?? []).map((p) => [p.languageCode, p]));

  // What the learner has chosen to study, in the order they added it. Codes
  // that no longer exist in the catalog are dropped rather than rendered as a
  // tile that opens onto nothing.
  const studying = settings.studyingLanguages
    .map((code) => byCode.get(code))
    .filter((l): l is Language => Boolean(l));

  const totalDue = studying.reduce(
    (sum, l) => sum + (progressByCode.get(l.code)?.dueCount ?? 0),
    0,
  );

  return (
    <main className="page">
      <header className="home-header">
        <h1>{firstName ? `Welcome back, ${firstName}` : 'Welcome back'}</h1>
        <p className="muted">
          {totalDue > 0
            ? `${totalDue} ${totalDue === 1 ? 'card is' : 'cards are'} ready for review.`
            : 'Carry on where you left off.'}
        </p>
      </header>

      {stats.data && (
        <section className="stat-row">
          <StatTile value={stats.data.currentStreak} label="day streak" />
          <StatTile value={stats.data.cardsReviewedToday} label="cards today" />
          <StatTile value={stats.data.cardsKnown} label="words known" />
          <StatTile value={stats.data.sectionsStudied} label="sections read" />
        </section>
      )}

      {stats.data && stats.data.totalSessions === 0 && (
        <p className="callout">
          Nothing studied yet. Open a section, read the grammar, then run the flashcards — the
          card setup is yours to change before you start.
        </p>
      )}

      <section>
        <div className="section-head">
          <h2>Your languages</h2>
          <Link to="/add-language" className="btn btn--small">
            Add a language
          </Link>
        </div>

        {languages.loading && <Loading what="languages" />}
        {languages.error && <LoadError message={languages.error} onRetry={languages.reload} />}

        {languages.data && studying.length === 0 && (
          <p className="callout">
            No languages yet. <Link to="/add-language">Add one</Link> to get started.
          </p>
        )}

        {studying.map((language) => (
          <LanguageCard
            key={language.code}
            language={language}
            progress={progressByCode.get(language.code)}
          />
        ))}
      </section>
    </main>
  );
}
